<template>
    <div class="container-fluid">
        <div class="row">
            <div class="col-sm-12">
                <div class="card">
                    <div class="card-body">
                        <div class="d-flex justify-content-between align-items-center">
                            <div>
                                <h4>Service product prices for {{ customer.company_name || '...' }}</h4>
                                <p class="text-muted mb-0">
                                    These prices will override the default service product prices for this customer.
                                </p>
                            </div>
                            <div>
                                <router-link
                                    :to="{ name: 'office.customers.show', params: { id: id } }"
                                    class="btn btn-outline-secondary"
                                >
                                    <i class="fa fa-arrow-left" /> Back to customer
                                </router-link>
                                <button
                                    class="btn btn-primary"
                                    @click="showAddModal"
                                >
                                    <i class="fa fa-plus" /> Add service product
                                </button>
                            </div>
                        </div>
                        <table
                            v-if="customerServiceProducts.length"
                            class="table mt-3"
                        >
                            <thead>
                                <tr>
                                    <th>Service Product</th>
                                    <th>Fitting Price</th>
                                    <th>ID Sign Price</th>
                                    <th>Custom Variations</th>
                                    <th class="text-right">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr
                                    v-for="csp in customerServiceProducts"
                                    :key="csp.id"
                                >
                                    <td>{{ csp.service_product ? csp.service_product.name : '' }}</td>
                                    <td>
                                        <template v-if="csp.fitting_price != null">
                                            £{{ csp.fitting_price | money }}
                                            <span
                                                v-if="csp.service_product && fittingDiff(csp) !== 0"
                                                :class="fittingDiff(csp) > 0 ? 'text-success' : 'text-danger'"
                                            >
                                                ({{ fittingDiff(csp) > 0 ? '+' : '' }}£{{ fittingDiff(csp) | money }})
                                            </span>
                                        </template>
                                        <span
                                            v-else
                                            class="text-muted"
                                        >Default</span>
                                    </td>
                                    <td>
                                        <template v-if="csp.id_sign_price != null">
                                            £{{ csp.id_sign_price | money }}
                                            <span
                                                v-if="csp.service_product && idSignDiff(csp) !== 0"
                                                :class="idSignDiff(csp) > 0 ? 'text-success' : 'text-danger'"
                                            >
                                                ({{ idSignDiff(csp) > 0 ? '+' : '' }}£{{ idSignDiff(csp) | money }})
                                            </span>
                                        </template>
                                        <span
                                            v-else
                                            class="text-muted"
                                        >Default</span>
                                    </td>
                                    <td>
                                        {{ countOverriddenVariations(csp) }} / {{ totalVariations(csp) }}
                                    </td>
                                    <td class="text-right">
                                        <button
                                            class="btn btn-sm btn-outline-info"
                                            type="button"
                                            @click="showEditModal(csp)"
                                        >
                                            <i class="fa fa-pencil-alt" /> Edit
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <p
                            v-else-if="!loading"
                            class="text-muted mt-3 mb-0"
                        >
                            No service product prices set for this customer.
                        </p>
                        <p
                            v-else
                            class="text-muted mt-3 mb-0"
                        >
                            Loading...
                        </p>
                    </div>
                </div>
            </div>
        </div>

        <customer-service-product-modal
            v-if="modalOpen"
            :customer-id="id"
            :service-products="serviceProducts"
            :customer-service-products="customerServiceProducts"
            :editing-product="modalEditingProduct"
            @saved="onModalSaved"
            @deleted="onModalDeleted"
            @close="modalOpen = false"
        />
    </div>
</template>
<script>
import CustomerService from '../../../services/CustomerService';
import CustomerServiceProductService from '../../../services/CustomerServiceProductService';
import ServiceProductsService from '../../../services/ServiceProductsService';
import UsesErrors from '../../../mixins/UsesErrors';
import UsesInfoMessage from '../../../mixins/UsesInfoMessage';

import CustomerServiceProductModal from './CustomerServiceProductModal.vue';

export default {
    components: {
        CustomerServiceProductModal,
    },
    mixins: [UsesErrors, UsesInfoMessage],
    props: {
        id: {
            type: [String, Number],
            required: true,
        },
    },
    data() {
        return {
            customer: {},
            serviceProducts: [],
            customerServiceProducts: [],
            loading: false,
            modalOpen: false,
            modalEditingProduct: null,
        };
    },
    mounted() {
        CustomerService.get(this.id).then(
            xhr => {
                this.customer = xhr.data;
            }
        );
        this.loadServiceProducts();
        this.loadCustomerServiceProducts();
        this.listenForUpdates();
    },
    beforeDestroy() {
        this.stopListeningForUpdates();
    },
    methods: {
        showAddModal() {
            this.modalEditingProduct = null;
            this.modalOpen = true;
        },
        showEditModal(csp) {
            this.modalEditingProduct = csp;
            this.modalOpen = true;
        },
        onModalSaved() {
            this.modalOpen = false;
            this.modalEditingProduct = null;
            this.loadCustomerServiceProducts();
        },
        onModalDeleted() {
            this.modalOpen = false;
            this.modalEditingProduct = null;
            this.loadCustomerServiceProducts();
        },
        fittingDiff(csp) {
            var original = parseFloat(csp.service_product && csp.service_product.fitting_price);
            var custom = parseFloat(csp.fitting_price);
            if (isNaN(original) || isNaN(custom)) return 0;
            return Math.round((custom - original) * 100) / 100;
        },
        idSignDiff(csp) {
            var original = parseFloat(csp.service_product && csp.service_product.id_sign_price);
            var custom = parseFloat(csp.id_sign_price);
            if (isNaN(original) || isNaN(custom)) return 0;
            return Math.round((custom - original) * 100) / 100;
        },
        countOverriddenVariations(csp) {
            if (!csp.variations) return 0;
            return csp.variations.filter(function (v) {
                return v.bs_price != null || v.rc_price != null || v.es_price != null || v.rcon_price != null || v.new_price != null;
            }).length;
        },
        totalVariations(csp) {
            if (!csp.service_product || !csp.service_product.variations) return 0;
            return csp.service_product.variations.length;
        },
        loadServiceProducts() {
            ServiceProductsService.index().then(function (res) {
                this.serviceProducts = res.data;
            }.bind(this));
        },
        loadCustomerServiceProducts() {
            this.loading = true;
            CustomerServiceProductService.index(this.id).then(function (res) {
                this.customerServiceProducts = res.data;
            }.bind(this)).finally(function () {
                this.loading = false;
            }.bind(this));
        },
        listenForUpdates() {
            if (!window.Echo) return;
            var channel = window.Echo.private('customer.' + this.id);
            channel.listen('CustomerServiceProductChanged', function () {
                this.loadCustomerServiceProducts();
            }.bind(this));
        },
        stopListeningForUpdates() {
            if (!window.Echo) return;
            window.Echo.leave('customer.' + this.id);
        },
    },
};
</script>



// WEBPACK FOOTER //
// resources/assets/js/components/office/customers/CustomerServiceProductsComponent.vue